import {
  BaseSelectProps,
  Checkbox,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
} from "@mui/material";
import { Select } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import React from "react";
import { SxProps } from "@mui/system";

export interface BNFormSelectProps extends BaseSelectProps<any> {
  id: string;
  label: string;
  options: Array<{ text: string; value: string | number }>;
  value?: any;
  disabled?: boolean;
  fullWidth?: boolean;
  className?: string;
  innerComponentClassName?: string;
  formControlSx?: SxProps;
  helperText?: string;
  helpLabel?: string;
  selectRef?: React.Ref<any>;
  emptyIfUnavailable?: boolean;
  placeholder?: string;
}

const BNSelect: React.FC<BNFormSelectProps> = (props) => {
  const {
    id,
    label,
    onChange,
    options,
    value,
    error,
    disabled,
    fullWidth,
    className,
    innerComponentClassName,
    helperText,
    selectRef,
    helpLabel,
    inputProps,
    formControlSx,
    emptyIfUnavailable,
    placeholder,
    ...restOfProps
  } = props;
  const theme = useTheme();

  let newValue = value;

  if (props.multiple && !(value instanceof Array)) {
    newValue = [];
  }

  return (
    <FormControl
      variant="standard"
      fullWidth={fullWidth}
      disabled={disabled}
      className={className}
      data-uit-container={`${id}`}
      error={error}
      sx={{
        minWidth: "min(21rem, 100%)",
        maxWidth: "100%",
        mt: 2,
        mb: 1,
        "& .MuiInputBase-formControl.MuiInputBase-formControl": {
          mt: 0.5,
        },
        ...formControlSx,
      }}
    >
      <InputLabel
        id={`${id}-label`}
        shrink={true}
        sx={{
          position: "relative",
          maxWidth: "100%",
          minHeight: theme.spacing(2.5),
          whiteSpace: "normal",
          overflowWrap: "anywhere",
        }}
      >
        <>
          {label}
          {helpLabel ? <i>({helpLabel})</i> : ""}
        </>
      </InputLabel>
      <Select
        sx={[
          {
            ".MuiSelect-select": {
              backgroundColor: theme.vars.palette.shadow.white,
              ...theme.applyStyles("dark", {
                backgroundColor: `color-mix(in srgb, ${theme.vars.palette.shadow.white} 10%, transparent)`,
              }),
            },
            ".MuiSelect-select.MuiSelect-select": {
              whiteSpace: "normal",
              overflowWrap: "anywhere",
            },
          },
        ]}
        labelId={`${id}-label`}
        value={
          newValue == undefined || (emptyIfUnavailable && !options.some((x) => x.value == newValue.toString()))
            ? ""
            : newValue
        }
        onChange={onChange}
        className={innerComponentClassName}
        displayEmpty={Boolean(placeholder)}
        renderValue={(selected) => {
          if (selected == null || selected === "") return placeholder;

          if (props.multiple) {
            const list: string[] = [];
            selected.forEach((x) => {
              // Don't use === as in some cases value is string and selected number. TODO add resilient typing
              const availableOption = options.find((y) => y.value == x)?.text;
              if (availableOption) list.push(availableOption);
            });
            return list.join(",");
            // Don't use === as in some cases value is string and selected number. TODO add resilient typing
          } else return options.find((y) => y.value == selected)?.text;
        }}
        inputProps={{
          name: `${id}-name`,
          id: id,
          ref: selectRef,
          ...inputProps,
        }}
        data-uit={`${id}`}
        {...restOfProps}
      >
        {placeholder && (
          <MenuItem key={`${id}-placeholder`} disabled>
            {placeholder}
          </MenuItem>
        )}
        {options.map((opt) => (
          <MenuItem
            key={`${id}-${opt.value}`}
            data-uit={`${id}-value-${opt.value}`}
            value={opt.value}
            sx={{ whiteSpace: "normal" }}
          >
            {props.multiple ? (
              <>
                <Checkbox color="secondary" checked={value.indexOf(opt.value) > -1} />
                <ListItemText primary={opt.text} />
              </>
            ) : (
              opt.text
            )}
          </MenuItem>
        ))}
      </Select>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};
export default BNSelect;
